import React, { useEffect, useMemo, useRef, useState } from "react";
import { optimizeImageFile } from "../../services/images/optimizeImage";
import { useIngredientes } from "../context/IngredientesContext";
import { useAccessControl } from "../context/useAccessControl";
import IngredientImage from "../common/IngredientImage";
import CatalogImage from "../common/CatalogImage";
import {
  readSessionUiState,
  removeSessionUiState,
  writeSessionUiState,
} from "../../services/local/uiSessionState";

const normalizePriceInput = (value) => {
  const normalized = value.replace(",", ".").replace(/[^0-9.]/g, "");
  const parts = normalized.split(".");

  if (parts.length <= 1) {
    return normalized;
  }

  return `${parts[0]}.${parts.slice(1).join("")}`;
};

const formatPrice = (value) => Number(value || 0).toFixed(2);

const buildIngredientesViewStateKey = (branchId) =>
  `mozzoapp:ingredientes-view:${branchId || "default"}`;

const fileToDataUrl = (file) => optimizeImageFile(file, { profile: "ingredient" });

const InputField = ({ value, onChange, placeholder, type = "text", disabled = false }) => (
  <input
    type={type}
    style={{
      padding: "8px",
      border: "1px solid #ececec",
      borderRadius: "8px",
      fontSize: "16px",
      width: "100%",
      maxWidth: "300px",
      marginBottom: "10px",
      display: "block",
      marginLeft: "auto",
      marginRight: "auto",
      boxSizing: "border-box",
      opacity: disabled ? 0.7 : 1,
    }}
    value={value}
    onChange={onChange}
    placeholder={placeholder}
    disabled={disabled}
  />
);

const Card = ({ children, onClick = null, interactive = false }) => (
  <div
    onClick={onClick || undefined}
    role={interactive ? "button" : undefined}
    tabIndex={interactive ? 0 : undefined}
    onKeyDown={
      interactive && onClick
        ? (event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onClick();
            }
          }
        : undefined
    }
    style={{
      backgroundColor: "#ffffff",
      borderRadius: "8px",
      boxShadow: "0px 4px 12px rgba(0,0,0,0.1)",
      padding: "20px",
      marginBottom: "20px",
      display: "inline-block",
      textAlign: "center",
      maxWidth: "100%",
      boxSizing: "border-box",
      cursor: interactive ? "pointer" : "default",
      transition: "transform 0.15s ease, box-shadow 0.15s ease",
      border: interactive ? "1px solid #dbe3ee" : "none",
    }}
  >
    {children}
  </div>
);

const Image = ({ image, label = "Imagen del ingrediente" }) => (
  <div
    role="img"
    aria-label={label}
    style={{
      width: "100px",
      height: "100px",
      backgroundImage: `url(${image})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      borderRadius: "8px",
      marginBottom: "10px",
      marginLeft: "auto",
      marginRight: "auto",
      backgroundColor: "#f3f4f6",
    }}
  />
);

const getBannerStyles = (storageMode, hasError) => {
  if (hasError) {
    return {
      backgroundColor: "#fff7ed",
      color: "#9a3412",
      border: "1px solid #fdba74",
    };
  }

  if (storageMode === "supabase") {
    return {
      backgroundColor: "#ecfeff",
      color: "#0f766e",
      border: "1px solid #99f6e4",
    };
  }

  return {
    backgroundColor: "#f8fafc",
    color: "#475569",
    border: "1px solid #cbd5e1",
  };
};

const Ingredientes = () => {
  const { currentBranch, canPerformAction } = useAccessControl();
  const {
    ingredientes,
    agregarIngrediente,
    actualizarIngrediente,
    eliminarIngrediente,
    loadingIngredientes,
    savingIngredientes,
    storageMode,
    syncError,
    connectionMessage,
    cargarIngredientes,
  } = useIngredientes();

  const [showForm, setShowForm] = useState(false);
  const [nombre, setNombre] = useState("");
  const [precio, setPrecio] = useState("");
  const [imageFile, setImageFile] = useState("");
  const [imageUploaded, setImageUploaded] = useState(false);
  const [readingImage, setReadingImage] = useState(false);
  const imageReadRequestRef = useRef(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [editing, setEditing] = useState(null);
  const [tipo, setTipo] = useState("obligatorio");
  const [seleccionable, setSeleccionable] = useState(true);
  // Permisos v1.1: ingredientes hereda toda su mutacion desde manage_menu.
  const canManageIngredientes = canPerformAction("manage_menu");
  const viewStateHydratedRef = useRef("");
  const ingredientesViewStateKey = useMemo(
    () => buildIngredientesViewStateKey(currentBranch?.id),
    [currentBranch?.id]
  );

  useEffect(() => {
    imageReadRequestRef.current += 1;
    setReadingImage(false);
    const savedViewState = readSessionUiState(ingredientesViewStateKey, null);

    setShowForm(Boolean(savedViewState?.showForm));
    setNombre(savedViewState?.nombre || "");
    setPrecio(savedViewState?.precio || "");
    setImageFile(savedViewState?.imageFile || "");
    setImageUploaded(Boolean(savedViewState?.imageUploaded));
    setSearchQuery(savedViewState?.searchQuery || "");
    setEditing(savedViewState?.editing || null);
    setTipo(savedViewState?.tipo || "obligatorio");
    setSeleccionable(savedViewState?.seleccionable ?? true);
    viewStateHydratedRef.current = ingredientesViewStateKey;
    return () => { imageReadRequestRef.current += 1; };
  }, [ingredientesViewStateKey]);

  useEffect(() => {
    if (viewStateHydratedRef.current !== ingredientesViewStateKey) {
      return;
    }

    writeSessionUiState(ingredientesViewStateKey, {
      showForm,
      nombre,
      precio,
      imageFile,
      imageUploaded,
      searchQuery,
      editing,
      tipo,
      seleccionable,
    });
  }, [
    editing,
    imageFile,
    imageUploaded,
    ingredientesViewStateKey,
    nombre,
    precio,
    searchQuery,
    seleccionable,
    showForm,
    tipo,
  ]);

  const resetForm = () => {
    imageReadRequestRef.current += 1;
    setReadingImage(false);
    setNombre("");
    setPrecio("");
    setTipo("obligatorio");
    setSeleccionable(true);
    setImageFile("");
    setImageUploaded(false);
    setEditing(null);
    setShowForm(false);
    removeSessionUiState(ingredientesViewStateKey);
  };

  const handleImageChange = async (e) => {
    if (!canManageIngredientes || savingIngredientes) {
      return;
    }

    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }

    if (file.type && !file.type.startsWith("image/")) {
      alert("Selecciona un archivo de imagen.");
      return;
    }

    const requestId = ++imageReadRequestRef.current;
    setReadingImage(true);
    try {
      const imageUrl = await fileToDataUrl(file);
      if (requestId !== imageReadRequestRef.current) return;
      setImageFile(imageUrl);
      setImageUploaded(true);
    } catch (error) {
      if (requestId === imageReadRequestRef.current) {
        alert(error.message || "No se pudo cargar la imagen.");
      }
    } finally {
      if (requestId === imageReadRequestRef.current) setReadingImage(false);
    }
  };

  const handleSubmit = async () => {
    if (!canManageIngredientes || savingIngredientes || readingImage) {
      return;
    }

    if (!nombre.trim() || !precio) {
      alert("Completa nombre y precio antes de guardar.");
      return;
    }

    try {
      await agregarIngrediente({
        nombre: nombre.trim(),
        precio,
        imagen: imageFile || "",
        tipo,
        seleccionable,
      });

      resetForm();
    } catch (error) {
      alert(error.message || "No se pudo guardar el ingrediente.");
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleEdit = (id) => {
    if (!canManageIngredientes) {
      return;
    }

    const ingrediente = ingredientes.find((item) => item.id === id);
    if (!ingrediente) {
      return;
    }

    imageReadRequestRef.current += 1;
    setReadingImage(false);
    setNombre(ingrediente.nombre);
    setPrecio(String(ingrediente.precio || ""));
    setImageFile(ingrediente.imagen);
    setTipo(ingrediente.tipo || "obligatorio");
    setSeleccionable(ingrediente.seleccionable !== false);
    setImageUploaded(Boolean(ingrediente.imagen));
    setEditing(id);
    setShowForm(true);
  };

  const handleUpdate = async () => {
    if (!canManageIngredientes || savingIngredientes || readingImage) {
      return;
    }

    if (!nombre.trim() || !precio || editing === null) {
      alert("Completa nombre y precio antes de actualizar.");
      return;
    }

    try {
      await actualizarIngrediente(editing, {
        nombre: nombre.trim(),
        precio,
        ...(imageFile ? { imagen: imageFile } : {}),
        tipo,
        seleccionable,
      });

      resetForm();
    } catch (error) {
      alert(error.message || "No se pudo actualizar el ingrediente.");
    }
  };

  const handleDelete = async (id) => {
    if (!canManageIngredientes) {
      return;
    }

    const confirmDelete = window.confirm(
      "Estas seguro de que quieres eliminar este ingrediente?"
    );

    if (!confirmDelete) {
      return;
    }

    try {
      await eliminarIngrediente(id);
    } catch (error) {
      alert(error.message || "No se pudo eliminar el ingrediente.");
    }
  };

  const filteredIngredientes = useMemo(
    () =>
      ingredientes.filter((ingrediente) =>
        ingrediente.nombre.toLowerCase().includes(searchQuery.toLowerCase())
      ),
    [ingredientes, searchQuery]
  );

  const statusBannerStyles = getBannerStyles(storageMode, Boolean(syncError));

  return (
    <div style={{ padding: "20px", textAlign: "center" }}>
      <h1>Gestion de Ingredientes</h1>

      {ingredientes.some((item) => item.starterTemplateKey) && (
        <section aria-label="Plantilla inicial de ingredientes" style={{
          maxWidth: 760, margin: "0 auto 20px", padding: "16px 20px",
          borderRadius: 12, textAlign: "left", backgroundColor: "#fff7ed",
          border: "1px solid #fed7aa", color: "#9a3412", lineHeight: 1.6,
        }}>
          <strong>Tu base de ingredientes ya esta cargada</strong>
          <p style={{ margin: "6px 0 0" }}>
            Conserva los ingredientes que uses, edita sus datos y elimina los que no necesites.
            La plantilla incluye nombres e imagenes, sin stock ni recetas. Los precios empiezan
            en cero y los extras desactivados: revisalos antes de armar tu carta.
          </p>
        </section>
      )}

      <div
        style={{
          ...statusBannerStyles,
          maxWidth: 760,
          margin: "0 auto 20px",
          padding: "12px 16px",
          borderRadius: 12,
          textAlign: "left",
          fontSize: 14,
        }}
      >
        <strong style={{ display: "block", marginBottom: 4 }}>
          {storageMode === "supabase" ? syncError ? "Sincronizacion pendiente" : "Supabase activo" : "Modo local"}
        </strong>
        <span>{connectionMessage}</span>
        {syncError && <button type="button" onClick={() => cargarIngredientes()} disabled={loadingIngredientes}
          style={{ display: "block", marginTop: 10, padding: "8px 16px", borderRadius: 8, cursor: "pointer" }}>
          {loadingIngredientes ? "Actualizando..." : "Reintentar carga"}
        </button>}
      </div>

      {!canManageIngredientes && (
        <div
          style={{
            maxWidth: 760,
            margin: "0 auto 20px",
            padding: "12px 16px",
            borderRadius: 12,
            textAlign: "left",
            fontSize: 14,
            backgroundColor: "#eff6ff",
            color: "#1d4ed8",
            border: "1px solid #bfdbfe",
          }}
        >
          Esta sucursal tiene acceso de solo lectura a ingredientes. Puedes revisar la base,
          pero no crear, editar ni eliminar.
        </div>
      )}

      {showForm ? (
        <div>
          <h2>{editing ? "Editar Ingrediente" : "Agregar Ingrediente"}</h2>
          <InputField
            value={nombre}
            onChange={(e) => setNombre(e.target.value)}
            placeholder="Nombre del ingrediente"
            disabled={savingIngredientes || !canManageIngredientes}
          />

          <label style={{ display: "block", width: "100%", maxWidth: 300, margin: "0 auto 10px", textAlign: "left" }}>
            <span style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
              {imageFile || editing ? "Cambiar imagen" : "Imagen del ingrediente"}
            </span>
            <input
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              disabled={savingIngredientes || readingImage || !canManageIngredientes}
              style={{
                padding: "8px",
                border: "1px solid #ececec",
                borderRadius: "8px",
                fontSize: "16px",
                width: "100%",
                boxSizing: "border-box",
                marginBottom: "10px",
                display: "block",
                marginLeft: "auto",
                marginRight: "auto",
              }}
            />
          </label>

          {readingImage && <p role="status">Optimizando imagen...</p>}
          <p style={{ fontSize: 13, color: "#64748b" }}>
            Foto opcional. Si no cargas una, se muestra la imagen predeterminada.
          </p>
          {!imageFile && !ingredientes.find((item) => item.id === editing)?.imageDeferred && (
            <CatalogImage alt="Vista previa del ingrediente"
              style={{ width: 100, height: 100, borderRadius: 8, margin: "0 auto 10px", display: "block" }} />
          )}

          {editing && !imageFile && ingredientes.find((item) => item.id === editing)?.imageDeferred && (
            <div>
              <IngredientImage ingredient={ingredientes.find((item) => item.id === editing)}
                label="Vista previa del ingrediente" style={{ width: 100, height: 100, borderRadius: 8, margin: "0 auto 10px" }} />
              <p style={{ fontSize: 13, color: "#64748b" }}>Puedes editar sin esperar la foto. Se conserva si no la reemplazas.</p>
            </div>
          )}

          {imageUploaded && imageFile && (
            <div>
              <p>Vista previa:</p>
              <Image image={imageFile} label="Vista previa del ingrediente" />
              <p style={{ fontSize: 13, color: "#64748b", margin: "0 auto 12px", maxWidth: 300 }}>
                La imagen se guarda al {editing ? "actualizar" : "guardar"} el ingrediente.
              </p>
            </div>
          )}

          <InputField
            value={precio}
            onChange={(e) => setPrecio(normalizePriceInput(e.target.value))}
            placeholder="Precio del ingrediente"
            type="number"
            disabled={savingIngredientes || !canManageIngredientes}
          />

          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            disabled={savingIngredientes || !canManageIngredientes}
            style={{
              padding: "8px",
              width: "300px",
              borderRadius: "8px",
              border: "1px solid #ececec",
              marginBottom: "10px",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              fontSize: "16px",
            }}
          >
            <option value="obligatorio">Obligatorio</option>
            <option value="opcional">Opcional</option>
          </select>

          <label style={{ display: "block", marginBottom: "10px", fontSize: "16px" }}>
            <input
              type="checkbox"
              checked={seleccionable}
              onChange={(e) => setSeleccionable(e.target.checked)}
              disabled={savingIngredientes || !canManageIngredientes}
              style={{ marginRight: "10px" }}
            />
            Disponible para ofrecer como extra en platos
          </label>

          <div
            style={{
              maxWidth: 420,
              margin: "0 auto 14px",
              fontSize: 13,
              color: "#64748b",
              lineHeight: 1.5,
            }}
          >
            Esto no significa que el cliente pueda usarlo en cualquier plato. Solo
            habilita que despues, al crear un plato, puedas ofrecerlo como extra si
            corresponde.
          </div>

          <Card>
            <div style={{ fontSize: "18px", fontWeight: "bold", color: "#1f2937" }}>
              {nombre || "Nombre del ingrediente"}
            </div>
            <div style={{ fontSize: "16px", color: "#111827" }}>
              ${formatPrice(precio)}
            </div>
            <div style={{ fontSize: "14px", color: "#6b7280" }}>
              {tipo === "opcional" ? "Opcional" : "Obligatorio"}
            </div>
            <div
              style={{
                fontSize: "14px",
                color: seleccionable ? "#10b981" : "#ef4444",
              }}
            >
              {seleccionable ? "Disponible para extras" : "No disponible para extras"}
            </div>
          </Card>

          <div style={{ marginBottom: "10px" }}>
            <button
              onClick={editing ? handleUpdate : handleSubmit}
              disabled={savingIngredientes || readingImage || !canManageIngredientes}
              style={{
                padding: "10px 20px",
                backgroundColor: "#5664f5",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: savingIngredientes || readingImage ? "wait" : "pointer",
                opacity: savingIngredientes || readingImage ? 0.75 : 1,
              }}
            >
              {readingImage
                ? "Optimizando imagen..."
                : savingIngredientes
                ? "Guardando..."
                : editing
                  ? "Actualizar Ingrediente"
                  : "Guardar Ingrediente"}
            </button>
          </div>
          <div style={{ marginBottom: "10px" }}>
            <button
              onClick={resetForm}
              disabled={savingIngredientes}
              style={{
                padding: "10px 20px",
                backgroundColor: "#e53e3e",
                color: "white",
                border: "none",
                borderRadius: "8px",
                cursor: savingIngredientes ? "not-allowed" : "pointer",
              }}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ marginBottom: "20px" }}>
            <input
              type="text"
              placeholder="Buscar ingrediente..."
              value={searchQuery}
              onChange={handleSearch}
              style={{
                padding: "8px",
                width: "300px",
                borderRadius: "8px",
                marginBottom: "10px",
                border: "1px solid #ececec",
                fontSize: "16px",
              }}
            />
          </div>

          {loadingIngredientes && !ingredientes.length ? (
            <div>Cargando ingredientes...</div>
          ) : filteredIngredientes.length > 0 ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "20px",
              }}
            >
              {filteredIngredientes.map((ingrediente) => (
                <Card
                  key={ingrediente.id}
                  interactive={canManageIngredientes}
                  onClick={
                    canManageIngredientes
                      ? () => handleEdit(ingrediente.id)
                      : null
                  }
                >
                  <div
                    style={{
                      cursor: canManageIngredientes ? "pointer" : "default",
                    }}
                  >
                    <IngredientImage ingredient={ingrediente} label={ingrediente.nombre}
                      style={{ width: 100, height: 100, borderRadius: 8, margin: "0 auto 10px" }} />
                  </div>
                  <div style={{ fontSize: "18px", fontWeight: "bold" }}>
                    {ingrediente.nombre}
                  </div>
                  <div style={{ fontSize: "16px", color: "#333" }}>
                    {ingrediente.starterTemplateKey && Number(ingrediente.precio || 0) === 0
                      ? "Precio por configurar"
                      : `$${formatPrice(ingrediente.precio)}`}
                  </div>
                  <div style={{ fontSize: "14px", color: "#6b7280" }}>
                    {ingrediente.tipo === "opcional" ? "Opcional" : "Obligatorio"}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      color: ingrediente.seleccionable ? "#10b981" : "#ef4444",
                    }}
                  >
                    {ingrediente.seleccionable
                      ? "Disponible para extras"
                      : "No disponible para extras"}
                  </div>
                  {canManageIngredientes && (
                    <div
                      style={{
                        marginTop: "10px",
                        display: "flex",
                        justifyContent: "center",
                        gap: "10px",
                      }}
                    >
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleEdit(ingrediente.id);
                        }}
                        disabled={savingIngredientes}
                        style={{
                          padding: "8px 16px",
                          backgroundColor: "#f59e0b",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          cursor: savingIngredientes ? "not-allowed" : "pointer",
                        }}
                      >
                        Editar
                      </button>
                      <button
                        onClick={(event) => {
                          event.stopPropagation();
                          handleDelete(ingrediente.id);
                        }}
                        disabled={savingIngredientes}
                        style={{
                          padding: "8px 16px",
                          backgroundColor: "#e53e3e",
                          color: "white",
                          border: "none",
                          borderRadius: "8px",
                          cursor: savingIngredientes ? "not-allowed" : "pointer",
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          ) : (
            <div>{syncError ? "La lista no se pudo actualizar. Reintenta la carga." : "No hay ingredientes para mostrar"}</div>
          )}

          {canManageIngredientes && (
            <div style={{ marginTop: "20px" }}>
              <button
                onClick={() => setShowForm(true)}
                disabled={savingIngredientes}
                style={{
                  padding: "10px 20px",
                  backgroundColor: "#5664f5",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: savingIngredientes ? "not-allowed" : "pointer",
                }}
              >
                Agregar Ingrediente
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Ingredientes;
